import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Trash2 } from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} €`;
const MEZZI = [["bonifico", "Bonifico"], ["pos", "POS / Carta"], ["contanti", "Contanti"], ["assegno", "Assegno"], ["riba_rid", "RiBa / RID"], ["altro", "Altro"]];
const today = () => new Date().toISOString().slice(0, 10);

const Riepilogo = ({ d }) => (
  <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="ddtpag-riepilogo">
    {[["Totale", d.totale, "ddtpag-totale"], ["Pagato", d.pagato, "ddtpag-pagato"], ["Residuo", d.residuo, "ddtpag-residuo"]].map(([l, v, t]) => (
      <div key={l} className="rounded-md border border-border p-2">
        <div className="text-muted-foreground">{l}</div>
        <div className={`font-bold tabular-nums ${l === "Residuo" && v > 0 ? "text-amber-700" : ""}`} data-testid={t}>{fmt(v)}</div>
      </div>
    ))}
    {d.iva > 0 && <div className="col-span-3 text-[11px] text-muted-foreground">Imponibile {fmt(d.imponibile)} + IVA {fmt(d.iva)}</div>}
  </div>
);

export const DdtPagamentoDialog = ({ did, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ importo: "", data_pagamento: today(), mezzo_pagamento: "bonifico" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/ddt/${did}/pagamenti`).then(({ data }) => setD(data)).catch(() => toast.error("DDT non disponibile"));
  useEffect(() => {
    if (!did) return;
    setD(null);
    setF({ importo: "", data_pagamento: today(), mezzo_pagamento: "bonifico" });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [did]);

  const salva = async (saldo) => {
    const importo = Number(String(f.importo).replace(",", "."));
    if (!saldo && !(importo > 0)) return toast.error("Scrivi l'importo pagato");
    setBusy(true);
    try {
      const { data } = await api.post(`/ddt/${did}/pagamenti`, { ...f, importo: saldo ? null : importo, saldo });
      setD(data); setF({ ...f, importo: "" });
      toast.success(data.fattura ? `Pagamento registrato sulla fattura #${data.fattura.numero}` : "Pagamento registrato");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const elimina = async (pid) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    try { const { data } = await api.delete(`/ddt/${did}/pagamenti/${pid}`); setD(data); onSaved?.(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={!!did} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="ddtpag-dialog">
        <DialogHeader><DialogTitle>Pagamento DDT {d ? `n. ${d.numero || "s.n."}` : ""}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
          <div className="space-y-4">
            <Riepilogo d={d} />
            {d.fattura && (
              <div className="text-xs rounded-md border border-sky-200 bg-sky-50 text-sky-800 p-2" data-testid="ddtpag-fattura">
                Collegato alla fattura #{d.fattura.numero}: il pagamento viene registrato sulla fattura. Rimanenza della fattura: <b>{fmt(d.fattura.residuo)}</b> su {fmt(d.fattura.totale)}.
              </div>
            )}
            {d.residuo > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Data</Label><Input type="date" value={f.data_pagamento} onChange={(e) => setF({ ...f, data_pagamento: e.target.value })} data-testid="ddtpag-data" /></div>
                  <div><Label>Metodo</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm" value={f.mezzo_pagamento} onChange={(e) => setF({ ...f, mezzo_pagamento: e.target.value })} data-testid="ddtpag-metodo">
                      {MEZZI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <Button className="w-full h-11" disabled={busy} onClick={() => salva(true)} data-testid="ddtpag-saldo">Saldo totale · {fmt(d.residuo)}</Button>
                <div className="flex gap-2">
                  <Input inputMode="decimal" placeholder="Importo parziale €" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} className="h-11" data-testid="ddtpag-importo" />
                  <Button variant="outline" className="h-11" disabled={busy} onClick={() => salva(false)} data-testid="ddtpag-parziale">Parziale</Button>
                </div>
              </div>
            ) : <div className="text-sm text-emerald-700 text-center" data-testid="ddtpag-saldato">DDT saldato.</div>}
            {d.pagamenti.length > 0 && (
              <div className="space-y-1" data-testid="ddtpag-lista">
                {d.pagamenti.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs border-t border-border pt-1">
                    <span>{(p.data_pagamento || "").split("-").reverse().join("/")} · {MEZZI.find(([v]) => v === p.mezzo_pagamento)?.[1] || p.mezzo_pagamento}</span>
                    <span className="flex items-center gap-1 font-bold tabular-nums">{fmt(p.importo)}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => elimina(p.id)} data-testid={`ddtpag-del-${p.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
